/**
 * MatchingSettings — Admin-Einstellungen → Matching.
 *
 * Admins stellen die Gewichte der Vergleichsfelder (0 = ignorieren,
 * 1 = Standard, bis 10) und die Standard-Anzahl der Kandidaten je Liste
 * selbst ein. Gespeichert wird über `matchingSettingsApi`; die Werte wirken
 * sofort in den Kandidatenlisten (/matching, Matching-Board, Widgets).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input } from '../../components/ui';
import { BRAND, getThemeColors } from '../../lib/theme';
import { matchingSettingsApi, MATCHING_TYPE_KEY, type MatchingSettings as Settings } from '../../lib/matchingSettings';

interface MatchingSettingsProps {
  dark: boolean;
}

const toNum = (v: string): number | null => {
  const n = Number(v.replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
};

export function MatchingSettings({ dark }: MatchingSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [settings, setSettings] = useState<Settings | null>(null);
  // Eingaben als Text, damit „2,5" und leere Felder beim Tippen erlaubt sind.
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [limit, setLimit] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const adopt = (s: Settings) => {
    setSettings(s);
    setWeights(Object.fromEntries(s.fields.map(f => [f.name, String(s.weights[f.name] ?? 1)])));
    setLimit(String(s.default_limit));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await matchingSettingsApi.get();
        if (!cancelled) adopt(s);
      } catch {
        if (!cancelled) setLoadError(tr('settings.matching.load_error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tr]);

  const limits = settings?.limits ?? { weight_min: 0, weight_max: 10, limit_min: 1, limit_max: 200 };

  /** Prüft alle Eingaben; null = gültig, sonst Fehlertext. */
  const validation = useMemo(() => {
    if (!settings) return null;
    for (const f of settings.fields) {
      const n = toNum(weights[f.name] ?? '');
      if (n === null || n < limits.weight_min || n > limits.weight_max) {
        return tr('settings.matching.error_weight', { field: tr(`pflege.form.field.${f.name}`), min: limits.weight_min, max: limits.weight_max });
      }
    }
    const l = toNum(limit);
    if (l === null || !Number.isInteger(l) || l < limits.limit_min || l > limits.limit_max) {
      return tr('settings.matching.error_limit', { min: limits.limit_min, max: limits.limit_max });
    }
    return null;
  }, [settings, weights, limit, limits, tr]);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return settings.fields.some(f => toNum(weights[f.name] ?? '') !== settings.weights[f.name])
      || toNum(limit) !== settings.default_limit;
  }, [settings, weights, limit]);

  const isDefault = useMemo(() => {
    if (!settings) return true;
    return settings.fields.every(f => toNum(weights[f.name] ?? '') === settings.defaults.weights[f.name])
      && toNum(limit) === settings.defaults.default_limit;
  }, [settings, weights, limit]);

  const change = (name: string, value: string) => {
    setWeights(prev => ({ ...prev, [name]: value }));
    setSaved(false); setSaveError(null);
  };

  const save = async () => {
    if (!settings || validation) { setSaveError(validation); return; }
    setSaving(true); setSaveError(null); setSaved(false);
    try {
      const updated = await matchingSettingsApi.update({
        weights: Object.fromEntries(settings.fields.map(f => [f.name, toNum(weights[f.name] ?? '') ?? 1])),
        default_limit: toNum(limit) ?? settings.default_limit,
      });
      adopt(updated);
      setSaved(true);
    } catch {
      setSaveError(tr('settings.matching.error_save'));
    } finally {
      setSaving(false);
    }
  };

  /** Felder auf die Defaults stellen (noch nicht gespeichert). */
  const fillDefaults = () => {
    if (!settings) return;
    setWeights(Object.fromEntries(settings.fields.map(f => [f.name, String(settings.defaults.weights[f.name] ?? 1)])));
    setLimit(String(settings.defaults.default_limit));
    setSaved(false); setSaveError(null);
  };

  const inputStyle: React.CSSProperties = { width: 88, textAlign: 'right' };

  return (
    <Card dark={dark} style={{ padding: 20, maxWidth: 760 }}>
      <SectionTitle
        dark={dark}
        title={tr('settings.matching.title')}
        subtitle={tr('settings.matching.subtitle')}
      />
      {loading ? (
        <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('common.loading')}</div>
      ) : loadError || !settings ? (
        <div style={{ fontSize: 12, color: '#b42318' }}>{loadError ?? tr('settings.matching.load_error')}</div>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: t.muted, lineHeight: 1.5, margin: '4px 0 14px' }}>
            {tr('settings.matching.hint', { min: limits.weight_min, max: limits.weight_max })}
          </div>

          <table data-testid="matching-weights" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: t.tableHeader }}>
                {[tr('settings.matching.th.field'), tr('settings.matching.th.type'), tr('settings.matching.th.weight')].map((h, i) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: i === 2 ? 'right' : 'left', fontSize: 11, fontWeight: 800, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${t.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {settings.fields.map(f => {
                const n = toNum(weights[f.name] ?? '');
                const ignored = n === 0;
                const changed = n !== settings.defaults.weights[f.name];
                const label = tr(`pflege.form.field.${f.name}`);
                return (
                  <tr key={f.name} style={{ borderTop: `1px solid ${t.borderSoft}`, opacity: ignored ? 0.6 : 1 }}>
                    <td style={{ padding: '7px 10px', color: t.heading, fontWeight: 600 }}>
                      {label}
                      {changed && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: BRAND, background: `${BRAND}1a`, borderRadius: 999, padding: '1px 7px' }}>
                          {ignored ? tr('settings.matching.badge_ignored') : tr('settings.matching.badge_changed')}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '7px 10px', color: t.muted, fontSize: 12 }}>
                      {f.fit
                        ? tr('settings.matching.type.fit')
                        : tr(MATCHING_TYPE_KEY[f.type] ?? 'settings.matching.type.generic', { type: f.type })}
                    </td>
                    <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                      <Input
                        dark={dark}
                        type="number"
                        min={limits.weight_min}
                        max={limits.weight_max}
                        step={0.5}
                        value={weights[f.name] ?? ''}
                        onChange={e => change(f.name, e.target.value)}
                        aria-label={`${tr('settings.matching.th.weight')}: ${label}`}
                        style={inputStyle}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: 18, maxWidth: 320 }}>
            <Field label={tr('settings.matching.limit_label', { min: limits.limit_min, max: limits.limit_max })} hint={tr('settings.matching.limit_hint')}>
              <Input
                dark={dark}
                type="number"
                min={limits.limit_min}
                max={limits.limit_max}
                step={1}
                value={limit}
                onChange={e => { setLimit(e.target.value); setSaved(false); setSaveError(null); }}
                aria-label={tr('settings.matching.limit_aria')}
              />
            </Field>
          </div>

          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="primary" size="sm" dark={dark} onClick={() => void save()} disabled={saving || !dirty || validation !== null}>
              {saving ? tr('settings.matching.saving') : tr('common.action.save')}
            </Button>
            <Button variant="secondary" size="sm" dark={dark} onClick={fillDefaults} disabled={saving || isDefault}>
              {tr('settings.matching.reset')}
            </Button>
            {saved && <span style={{ fontSize: 12, color: '#039855', fontWeight: 600 }}>{tr('settings.matching.saved')}</span>}
            {(saveError ?? (dirty ? validation : null)) && (
              <span role="alert" style={{ fontSize: 12, color: '#b42318', fontWeight: 600 }}>{saveError ?? validation}</span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
